//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { yupErc20WeiRequired } from './Enums'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'
import { ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export const PoolAccountOnlyCTOSchemaValues = {
  earlyWithdrawInterestAdjustment: yupErc20WeiRequired
    .label('Early Withdraw Interest Adjustment')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyWithdrawInterestAdjustment',

        description: 'Interest earned adjustment due to early withdraw ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  maxWithdrawRequest: yupErc20WeiRequired
    .label('Max Withdrawal Request')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxWithdrawRequest',
        description:
          'The maximum amount of assets that can be requested for withdrawal. This is calculated multiplying the vault token balance by the exchange rate at maturity less any outstanding withdrawals, including early withdrawals and rollover terminations. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  maxRedeemRequest: yupErc20WeiRequired
    .label('Max Token Redemption Request')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxRedeemRequest',
        description:
          'The maximum amount of vault tokens that can be requested for withdrawal. This is the vault token balance less any outstanding withdrawal requests, including early withdrawals and rollover terminations.',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  maxRequestCancellation: yupErc20WeiRequired
    .label('Max Request Cancellation ')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxRequestCancellation',
        description: '',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  maxWithdraw: yupErc20WeiRequired
    .label('Maximum Withdrawal Amount')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxWithdraw',
        description: 'Same as withdrawableAssetsOf',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  maxRedeem: yupErc20WeiRequired
    .label('Max Number of Tokens Redeemable')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxRedeem',
        description: 'Same as redeemableSharesOf',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  requestedSharesOf: yupErc20WeiRequired
    .label('Requested Shares of Account')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedSharesOf',
        description: 'The amount of vault tokens included in rollover termination requests. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  redeemableSharesOf: yupErc20WeiRequired
    .label('Redeemable Shares of Account')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'redeemableSharesOf',
        description:
          'The maximum amount of vault tokens that can currently be redeemed for assets immediately. This equals the requestedSharesOf multiplied by the exchange rate at maturity once the loan has been repaid into the loan vault by the Borrower.',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  requestedAssetsOf: yupErc20WeiRequired
    .label('(Removed Total) Rollover Terminations Outstanding')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedAssetsOf',
        description:
          'The amount of assets included in rollover termination requests that have yet to be withdrawn. This is calculated by multiplying the requestedSharesOf by the exchange rate at maturity. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  requestedPrincipalOf: yupErc20WeiRequired
    .label(' Rollover Terminations Principal Outstanding')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedPrincipalOf',
        description:
          'The amount of assets included in rollover termination requests that have yet to be withdrawn. This is calculated by multiplying the requestedSharesOf by the exchange rate at time of loan. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  withdrawableAssetsOf: yupErc20WeiRequired
    .label('Withdrawable Assets of Account')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'withdrawableAssetsOf',
        description:
          'The maximum amount of assets that can currently be withdrawn. This equals the requestedAssetsOf multiplied by the exchange rate at maturity once the loan has been repaid into the loan vault by the Borrower. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  earlyRequestedSharesOf: yupErc20WeiRequired
    .label('earlyRequestedSharesOf')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyRequestedSharesOf',
        description:
          'The amount of vault tokens included in all outstanding early withdrawals requests that have not been accepted. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  earlyRequestedAssetsOf: yupErc20WeiRequired
    .label('Total Early Withdrawals Requested at Maturity')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyRequestedAssetsOf',
        description:
          'The total amount of early withdrawals requests that have not yet been accepted expressed as their value if held to maturity. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) that have been requested but not accepted (“earlyRequestedSharesOf”) by the exchange rate at maturity (“exchangeRateAtMaturity”). ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  earlyRequestedPrincipalOf: yupErc20WeiRequired
    .label('*** Total Early Withdrawals Principal Requested')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyRequestedPrincipalOf',
        description:
          'The total amount of loan principal in early withdrawals requests that have not yet been accepted. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) that have been requested but not accepted (“earlyRequestedSharesOf”) by the exchange rate at the start of the loan cycle. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  earlyAcceptedSharesOf: yupErc20WeiRequired
    .label('earlyAcceptedSharesOf')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyAcceptedSharesOf',
        description:
          'The total amount of vault tokens in early withdrawals requests that have been accepted but not yet repaid. This amount decreases when early withdrawals are repaid. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  earlyAcceptedAssetsOf: yupErc20WeiRequired
    .label('earlyAcceptedAssetsOf')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyAcceptedAssetsOf',
        description:
          'The total amount of assets in early withdrawals requests that have been accepted but not yet repaid. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) (“earlyAcceptedSharesOf”) by the exchange rate at maturity. This amount decreases when early withdrawals are repaid. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  earlyAcceptedPrincipalOf: yupErc20WeiRequired
    .label('earlyAcceptedPrincipalOf')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'earlyAcceptedPrincipalOf',
        description:
          'The total amount of vault tokens in early withdrawals requests that have been accepted but not yet repaid. This is calculated by multiplying the amount of vault tokens in the early withdrawal request(s) (“earlyAcceptedSharesOf”) by the exchange rate at the start of the loan cycle. This value is included in principalTransferringOut.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  principalEarningInterest: yupErc20WeiRequired
    .label('Principal Earning Interest')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'principalEarningInterest',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description:
          'The amount of assets currently earning finalAPR. For rollover terminations, the rollover termination amount (“requestedAssetsOf”) is included in this value until the maturity date. For early withdrawals, principalEarningInterest is reduced when the early withdrawal is accepted. It is reduced by the amount of loan principal in accepted early withdrawal requests (“earlyAcceptedPrincipalOf”)',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  assetsTransferringIn: yupErc20WeiRequired
    .label('Assets Transferring In')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsTransferringIn',
        description:
          'The current amount of Assets that have been loaned and will be included in the next loan cycle, but are not currently earning interest.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  assetsTransferringOut: yupErc20WeiRequired
    .label('Assets Transferring Out')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsTransferringOut',
        description:
          'The current amount of Assets currently transferring out, expressed as their value if held to maturity. This value is the sum of requestedAssetsOf once the loan reaches maturity and earlyAcceptedAssetsOf. Note: Rollover terminations will be included in this amount once the loan reaches maturity and the funds begin the transfer out process and will decrease when assets are withdrawn from the vault. For Early Withdrawals, earlyAcceptedAssetsOf is included in this value as soon as the early withdrawal request is accepted and will decrease as soon as the Early Withdrawal is repaid.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  principalTransferringOut: yupErc20WeiRequired
    .label('*** Principal Transferring Out')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'principalTransferringOut',
        description:
          'The amount of loan principal in the transfer out process. This value does not include any accrued interest or expected interest. For rollover terminations, this amount will increase when the loan reaches maturity with outstanding rollover terminations and decrease when the Lender redeems vault tokens and receives USDC. For early withdrawal requests, this amount will increase when the early withdrawal requests are accepted and decrease when the early withdrawal is repaid. Calculated as requestedSharesOf at loan maturity, multiplied by the exchange rate at start plus earlyAcceptedSharesOf multiplied by the exchange rate at start ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  interestAccruedInActiveLoan: yupErc20WeiRequired
    .label('Interest Accrued in Active Loan')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'interestAccruedInActiveLoan',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
        description:
          'The current amount of interest accrued in the current loan cycle based on the principalEarningInterest',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  expectedInterestAtMaturity: yupErc20WeiRequired
    .label('Expected Interest At Maturity')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'expectedInterestAtMaturity',
        description:
          'The expected amount of interest to be accrued in the current loan cycle based on the principalEarningInterest and finalAPR',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  totalAssetsDeposited: yupErc20WeiRequired
    .label('All Assets Deposited in pool')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'totalAssetsDeposited',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 13,
        description: 'The total amount of USDC that the account has deposited since vault inception. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  totalAssetsWithdrawn: yupErc20WeiRequired
    .label('All Assets Withdrawn From Pool')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'totalAssetsWithdrawn',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 14,
        description: 'The total amount of USDC that the account has been withdrawn since vault inception. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  totalAssetsAtMaturity: yupErc20WeiRequired
    .label('Value of Assets at Maturity')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'totalAssetsAtMaturity',
        description:
          'The total value of vault tokens the account holds, which is calculated by multiplying the vault token balance by exchange rate at maturity. This equals the sum of principalEarningInterest, expectedInterestAtMaturity, and assetsTransferringIn.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  totalAssetCurrent: yupErc20WeiRequired
    .label(' *** Assets Current Value prorated to the maturity date')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'totalAssetCurrent',
        description:
          'The total value of vault tokens the account holds currently, which is calculated by multiplying the vault token balance by the prorated exchange rate. This equals the sum of principalEarningInterest, interestAccrued in ActiveLoan, and assetsTransferringIn. Please note that early withdrawals are not guaranteed to pay the interest Accrued in ActiveLoan as the early withdrawal amount will be determined by market prices when loan collateral is liquidated. ',

        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  interestInDay: yupErc20WeiRequired
    .label('Interest on Day')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'interestInDay',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
        description: 'Interest earned in the day',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
}

export const PoolTermAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolAccountOnlyCTOSchemaValues,
  })
  .label('PoolAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolAccountCTO', description: 'Pool Account CTO' }))

export const PoolTermAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolTermAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolTermAccountSummaryCTO',
      map: 'PoolAccountCTO',
      description: 'Pool Term Account CTO',
    })
  )

export interface PoolAccountSummaryCTO extends InferType<typeof PoolTermAccountSummaryCTOSchema> {}
export interface PoolTermAccountSummaryCTO extends InferType<typeof PoolTermAccountSummaryCTOSchema> {}
export interface PoolAccountCTO extends PoolBaseAccountCTO {
  earlyWithdrawInterestAdjustment: ERC20Wei
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  maxRequestCancellation: ERC20Wei
  maxWithdraw: ERC20Wei
  maxRedeem: ERC20Wei
  requestedSharesOf: ERC20Wei
  redeemableSharesOf: ERC20Wei
  requestedPrincipalOf: ERC20Wei
  requestedAssetsOf: ERC20Wei
  withdrawableAssetsOf: ERC20Wei
  earlyRequestedSharesOf: ERC20Wei
  earlyRequestedAssetsOf: ERC20Wei
  earlyRequestedPrincipalOf: ERC20Wei
  earlyAcceptedSharesOf: ERC20Wei
  earlyAcceptedAssetsOf: ERC20Wei
  earlyAcceptedPrincipalOf: ERC20Wei
  principalEarningInterest: ERC20Wei
  assetsTransferringIn: ERC20Wei
  assetsTransferringOut: ERC20Wei
  principalTransferringOut: ERC20Wei
  interestAccruedInActiveLoan: ERC20Wei
  expectedInterestAtMaturity: ERC20Wei
  totalAssetsDeposited: ERC20Wei
  totalAssetsWithdrawn: ERC20Wei
  totalAssetsAtMaturity: ERC20Wei
  totalAssetCurrent: ERC20Wei
  interestInDay: ERC20Wei
}

type PoolTermAccountCTOInfer = InferType<typeof PoolTermAccountCTOSchema>
export const _testAPoolTermAccount: PoolAccountCTO = {} as unknown as PoolTermAccountCTOInfer
export const _testBPoolTermAccount: PoolTermAccountCTOInfer = {} as unknown as PoolAccountCTO
